import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit3, FileText, Image, Palette, CheckCircle, Clock } from 'lucide-react';

const operations = [
  {
    id: 'generate_content',
    name: 'Generate Content',
    description: 'Create written content based on outlines and research',
    icon: FileText,
    color: 'orange',
    input: 'Content outline, style guidelines',
    output: 'Written content',
    example: 'Blog outline → Full blog post'
  },
  {
    id: 'create_visuals',
    name: 'Create Visuals',
    description: 'Design accompanying visuals and graphics',
    icon: Image,
    color: 'blue',
    input: 'Content theme, visual requirements',
    output: 'Visual assets',
    example: 'Article theme → Infographics and images'
  },
  {
    id: 'design_assets',
    name: 'Design Assets',
    description: 'Create professional design elements and branding',
    icon: Palette,
    color: 'purple',
    input: 'Brand guidelines, asset specifications',
    output: 'Design assets',
    example: 'Brand brief → Logo and design system'
  }
];

const getColorClasses = (color: string) => {
  const colors = {
    blue: { bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]', badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', hover: 'hover:bg-[var(--slate-dim)]' },
    purple: { bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]', badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', hover: 'hover:bg-[var(--slate-dim)]' },
    orange: { bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]', badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', hover: 'hover:bg-[var(--mustard-dim)]' }
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

export default function Creating() {
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  const handleOperation = async (operationId: string) => {
    setSelectedOperation(operationId);
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setResults(prev => [...prev, { id: Date.now(), operation: operationId, status: 'completed', timestamp: new Date().toISOString() }]);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/elevation/workflow" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Workflow
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[var(--mustard-dim)] flex items-center justify-center">
              <Edit3 className="h-6 w-6 text-[var(--mustard)]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[var(--text-pri)]">Creating Content</h1>
              <p className="text-[var(--text-sec)]">Author and design the final content</p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Studio Engine Status</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[var(--green)] rounded-full"></div>
              <span className="text-sm text-[var(--text-sec)]">Online</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-sm text-[var(--text-sec)]">Last activity: 5 minutes ago</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-[var(--green)]" />
              <span className="text-sm text-[var(--text-sec)]">Ready for operations</span>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Available Operations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {operations.map((operation) => {
              const Icon = operation.icon;
              const colors = getColorClasses(operation.color);
              
              return (
                <div key={operation.id} className={`${colors.bg} ${colors.border} border-2  p-6 ${colors.hover} transition-all duration-300`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 ${colors.badge}  flex items-center justify-center`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${colors.text}`}>{operation.name}</h3>
                      <p className={`text-sm ${colors.text}`}>{operation.description}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">INPUT:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.input}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">OUTPUT:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.output}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">EXAMPLE:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.example}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleOperation(operation.id)}
                    disabled={isProcessing}
                    className={`w-full py-2 px-4  font-medium transition-colors ${
                      isProcessing ? 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed' : `${colors.badge} ${colors.text} hover:opacity-80`
                    }`}
                  >
                    {isProcessing && selectedOperation === operation.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </div>
                    ) : (
                      'Execute Operation'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {results.length > 0 && (
          <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Processing Results</h2>
            <div className="space-y-3">
              {results.map((result) => (
                <div key={result.id} className="flex items-center gap-3 p-3 bg-[var(--surface)]">
                  <CheckCircle className="h-5 w-5 text-[var(--green)]" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[var(--text-pri)]">
                      {operations.find(op => op.id === result.operation)?.name} completed
                    </p>
                    <p className="text-xs text-[var(--text-ter)]">{new Date(result.timestamp).toLocaleString()}</p>
                  </div>
                  <span className="text-xs bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-2 py-1 rounded">{result.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Next Steps</h3>
          <p className="text-[var(--text-sec)] mb-4">Once content is created, move to the applying phase to integrate and format it.</p>
          <Link to="/elevation/workflow/applying" className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors">
            Go to Applying Phase
            <ArrowLeft className="h-4 w-4 rotate-180" />
          </Link>
        </div>
      </div>
    </div>
  );
}
